import type { GrammarState, HighlighterCore, LanguageRegistration } from "shiki/core";

/**
 * Syntax highlighting of code artefacts (openspec change `artefacts-viewer`, design §4a): Shiki's
 * fine-grained core with the JavaScript regex engine (no WASM), loaded on first use and never in
 * the app's entry chunk. Only tokens come back: the renderer makes each one a text node, so model
 * output never becomes markup. Anything unexpected falls back to plain text.
 */

/** One token: its text and the only styles kept (Shiki's two theme colours and font style). */
export interface CodeToken {
  content: string;
  style: Readonly<Record<string, string>>;
}
export type CodeLines = readonly (readonly CodeToken[])[];

type LangModule = { default: LanguageRegistration[] };

// Each grammar is its own lazy chunk; the keys are Shiki's language names.
const GRAMMARS: Record<string, () => Promise<LangModule>> = {
  bash: () => import("@shikijs/langs/bash"),
  c: () => import("@shikijs/langs/c"),
  cpp: () => import("@shikijs/langs/cpp"),
  csharp: () => import("@shikijs/langs/csharp"),
  css: () => import("@shikijs/langs/css"),
  diff: () => import("@shikijs/langs/diff"),
  docker: () => import("@shikijs/langs/docker"),
  go: () => import("@shikijs/langs/go"),
  html: () => import("@shikijs/langs/html"),
  ini: () => import("@shikijs/langs/ini"),
  java: () => import("@shikijs/langs/java"),
  javascript: () => import("@shikijs/langs/javascript"),
  json: () => import("@shikijs/langs/json"),
  jsx: () => import("@shikijs/langs/jsx"),
  kotlin: () => import("@shikijs/langs/kotlin"),
  markdown: () => import("@shikijs/langs/markdown"),
  php: () => import("@shikijs/langs/php"),
  powershell: () => import("@shikijs/langs/powershell"),
  python: () => import("@shikijs/langs/python"),
  ruby: () => import("@shikijs/langs/ruby"),
  rust: () => import("@shikijs/langs/rust"),
  scala: () => import("@shikijs/langs/scala"),
  sql: () => import("@shikijs/langs/sql"),
  swift: () => import("@shikijs/langs/swift"),
  toml: () => import("@shikijs/langs/toml"),
  tsx: () => import("@shikijs/langs/tsx"),
  typescript: () => import("@shikijs/langs/typescript"),
  xml: () => import("@shikijs/langs/xml"),
  yaml: () => import("@shikijs/langs/yaml"),
};

/** Common spellings of an artefact's language token, mapped to a grammar. */
const ALIASES: Record<string, string> = {
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  "c++": "cpp",
  "c#": "csharp",
  cs: "csharp",
  dockerfile: "docker",
  golang: "go",
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  kt: "kotlin",
  md: "markdown",
  ps1: "powershell",
  py: "python",
  rb: "ruby",
  rs: "rust",
  ts: "typescript",
  yml: "yaml",
  patch: "diff",
};

/** The grammar of an artefact's language, or null (shown as plain text). */
export function grammarOf(language: string | null | undefined): string | null {
  if (!language) {
    return null;
  }
  const key = language.toLowerCase();
  const name = ALIASES[key] ?? key;
  return Object.hasOwn(GRAMMARS, name) ? name : null;
}

/** Above these, a version is shown as plain text ("Highlighting is off for large files"). */
export const HIGHLIGHT_LIMITS = { lines: 2_000, chars: 50_000 } as const;

export function tooLargeToHighlight(code: string): boolean {
  if (code.length > HIGHLIGHT_LIMITS.chars) {
    return true;
  }
  let lines = 1;
  for (let i = 0; i < code.length; i++) {
    if (code.charCodeAt(i) === 10 && ++lines > HIGHLIGHT_LIMITS.lines) {
      return true;
    }
  }
  return false;
}

let highlighter: Promise<HighlighterCore> | null = null;

function core(): Promise<HighlighterCore> {
  highlighter ??= (async () => {
    const [{ createHighlighterCore }, { createJavaScriptRegexEngine }] = await Promise.all([
      import("shiki/core"),
      import("shiki/engine/javascript"),
    ]);
    return createHighlighterCore({
      themes: [import("@shikijs/themes/github-light"), import("@shikijs/themes/github-dark")],
      langs: [],
      // `forgiving`: a pattern the engine cannot translate is skipped instead of failing the file.
      engine: createJavaScriptRegexEngine({ forgiving: true }),
    });
  })();
  // A failed load (offline, a missing chunk) is retried on the next code artefact.
  highlighter.catch(() => {
    highlighter = null;
  });
  return highlighter;
}

// Versions are immutable: tokens are kept per artefact version for the session (a few entries).
const memo = new Map<string, Promise<CodeLines | null>>();
const MEMO_ENTRIES = 16;

/**
 * The tokens of `code` in `language`, or null when it is shown as plain text (unknown language,
 * too large, or any failure). `key` names the artefact version (memoised).
 */
export function highlightCode(
  key: string,
  code: string,
  language: string | null,
  budget: Partial<HighlightBudget> = {},
): Promise<CodeLines | null> {
  const known = memo.get(key);
  if (known) {
    return known;
  }
  const result = tokenize(code, language, { ...HIGHLIGHT_BUDGET, ...budget });
  memo.set(key, result);
  if (memo.size > MEMO_ENTRIES) {
    memo.delete(memo.keys().next().value as string);
  }
  // A failure is not remembered: the next open tries again.
  void result.then((lines) => {
    if (lines === null) {
      memo.delete(key);
    }
  });
  return result;
}

/**
 * The main thread's budget for one version. Lines are tokenized a few at a time, the thread is
 * yielded between chunks, and each chunk's per-line limit is cut so that it cannot overrun what
 * is left: the tokenizer's own time spent stays near `totalMs` (one regex match is never
 * interrupted, so a single pathological match can still exceed it), then the version is plain.
 */
export interface HighlightBudget {
  /** Tokenizing time across all lines, in ms. */
  totalMs: number;
  /** Shiki's per-line limit (`tokenizeTimeLimit`), in ms, at most. */
  lineMs: number;
  /** Lines per `codeToTokens` call; the grammar's state carries across calls. */
  chunkLines: number;
  /** The clock (a test seam). */
  now: () => number;
}

export const HIGHLIGHT_BUDGET: HighlightBudget = {
  totalMs: 2_000,
  lineMs: 100,
  chunkLines: 32,
  now: () => performance.now(),
};

async function tokenize(
  code: string,
  language: string | null,
  budget: HighlightBudget,
): Promise<CodeLines | null> {
  const lang = grammarOf(language);
  if (!lang || tooLargeToHighlight(code)) {
    return null;
  }
  try {
    const shiki = await core();
    if (!shiki.getLoadedLanguages().includes(lang)) {
      await shiki.loadLanguage((await GRAMMARS[lang]?.())?.default ?? []);
    }
    const lines = code.split(/\r?\n/);
    const out: CodeLines[number][] = [];
    let grammarState: GrammarState | undefined;
    let spent = 0;
    for (let at = 0; at < lines.length; at += budget.chunkLines) {
      const chunk = lines.slice(at, at + budget.chunkLines);
      const left = budget.totalMs - spent;
      // 0 would mean "no limit" to Shiki: at least 1 ms per line.
      const lineMs = Math.max(1, Math.min(budget.lineMs, Math.floor(left / chunk.length)));
      const started = budget.now();
      const result = shiki.codeToTokens(chunk.join("\n"), {
        lang,
        themes: { light: "github-light", dark: "github-dark" },
        defaultColor: false,
        colorReplacements: THEME_FIXES,
        tokenizeMaxLineLength: 1_000,
        tokenizeTimeLimit: lineMs,
        grammarState,
      });
      spent += budget.now() - started;
      if (spent >= budget.totalMs) {
        return null;
      }
      grammarState = result.grammarState;
      for (const line of result.tokens) {
        out.push(
          line.map((token) => ({ content: token.content, style: safeStyle(token.htmlStyle) })),
        );
      }
      if (at + budget.chunkLines < lines.length) {
        // Let the page paint and respond between chunks.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    }
    return out;
  } catch {
    // Never shown: the plain text is the answer. No content in logs.
    return null;
  }
}

/**
 * Token colours that miss AA (4.5:1) on the code block's ground (`surface-sunken`), replaced by
 * the same hue's next step; and foregrounds the themes pair with a background this renderer
 * drops (invalid and ignored markup), which would otherwise vanish on the ground.
 */
const THEME_FIXES = {
  "github-light": {
    "#d73a49": "#b31d28",
    "#6a737d": "#57606a",
    "#e36209": "#a14800",
    "#22863a": "#1a6d31",
    "#fafbfc": "#b31d28",
    "#f6f8fa": "#24292e",
  },
  "github-dark": { "#6a737d": "#959da5", "#24292e": "#fdaeb7", "#2f363d": "#e1e4e8" },
};

const STYLE_KEY = /^--shiki-(?:light|dark)(?:-font-style|-font-weight|-text-decoration)?$/;
const STYLE_VALUE = /^(?:#[0-9a-f]{3,8}|italic|bold|underline|normal|none|inherit)$/i;

/** Only Shiki's theme variables with a colour or a font keyword: nothing else reaches `style`. */
function safeStyle(style: Record<string, string> | undefined): Record<string, string> {
  const kept: Record<string, string> = {};
  for (const [name, value] of Object.entries(style ?? {})) {
    if (STYLE_KEY.test(name) && STYLE_VALUE.test(value)) {
      kept[name] = value;
    }
  }
  return kept;
}

/** Test seam: forgets the memo and the loaded highlighter. */
export function resetHighlighter() {
  memo.clear();
  highlighter = null;
}
